// React
import React, { useContext, useMemo } from "react";

// Types
import { ProductBuilderSelection } from "../../../../types/ProductBuilderTypes";

// Utils
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { PluginDataContext } from "../../../../pluginsInfrastructure/PluginDataContextProvider";
import PageConfig from "../../../../utils/PageConfig";

import {
  ProductChoiceChangedEvent,
  ProductOption,
  ProductOptionProps,
} from "@vp/product-options-ui";
import { mapMpvValueToProductOptionChoice } from "../../utils/productOptionChoice";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";

interface DropdownSelectorProps {
  selectorIndex: number;
  selectionData: ProductBuilderSelection;
  showIncompatibleValues: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}

const DropdownSelector = (props: DropdownSelectorProps) => {
  const { compatibleValues, attributeConstraints, prices } = useContext(BffDataContext);
  const { selections = {} } = useContext(ProductContext);
  const { pluginData } = useContext(PluginDataContext);
  const { locale } = useContext(PageStaticDataContext);

  const option = props.selectionData.property;
  const shouldRender =
    option && compatibleValues[option.key] && compatibleValues[option.key].length !== 0;

  const optionProps = useMemo(() => {
    if (!shouldRender) {
      return undefined;
    }

    const dropdownIncompatibleMessage = pluginData.contentful["dropdownIncompatibleMessage"] ?? "";
    const outOfStockButtonAttribute = pluginData.contentful["outOfStockButtonAttribute"] ?? "";
    const newCalloutText = pluginData.contentful["newCalloutText"] ?? "";
    const recommendedText = pluginData.contentful["recommended"] ?? "";
    const currency = Object.values(prices)?.[0]?.currency ?? "";
    const fractionDigits = Object.values(prices)?.[0]?.fractionDigits ?? 2;

    const mappedOptions: ProductOptionProps = {
      productOption: {
        key: option.key,
        name: option.name,
        style: "dropdown",
        selectedKey: selections[option.key],
        choices: (option.options || [])
          .map(optionValue =>
            mapMpvValueToProductOptionChoice({
              optionValue,
              option,
              selections,
              compatibleValues,
              attributeConstraints,
              showIncompatibleValues: props.showIncompatibleValues,
              dictionary: {
                outOfStockText: outOfStockButtonAttribute,
                selectorIncompatibleText: dropdownIncompatibleMessage,
                newCalloutText,
                recommendedText,
              },
            })
          )
          .map(optionChoice => {
            // This dropdown skin is special in desiring not to showcase the incompatible message per item
            if (optionChoice.callouts?.find(x => x.text === dropdownIncompatibleMessage)) {
              return Object.assign({}, optionChoice, {
                callouts: optionChoice.callouts?.filter(
                  x => x.text !== dropdownIncompatibleMessage
                ),
              });
            }
            return optionChoice;
          }),
      },
      skin: "minimal",
      hideImages: true,
      tenant: PageConfig.tenant,
      locale: locale,
      currency: currency,
      fractionDigits: fractionDigits,
      onChanged: (event: ProductChoiceChangedEvent) => {
        if (event.choiceKey) {
          props.onChange(event.choiceKey);
        }
      },
      showIncompatibleChoices: props.showIncompatibleValues,
      priceDisplayMode: "none",
    };

    return mappedOptions;
  }, [
    pluginData.contentful,
    prices,
    compatibleValues,
    attributeConstraints,
    selections,
    locale,
    option,
    props.showIncompatibleValues,
    props.selectorIndex,
    shouldRender,
  ]);

  if (!shouldRender || !optionProps) {
    return <></>;
  }

  return <ProductOption {...optionProps} />;
};

export default DropdownSelector;
